import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import isNil from 'lodash/isNil';

export const QUERY_STRING_BUDGET_BYTES = 1800;

const DEFAULT_BATCH_CONCURRENCY = 4;

type BatchQueryResult = { data?: unknown; error?: FetchBaseQueryError };

export const chunkIdsForQuery = (
  ids: string[],
  paramName: string,
  budgetBytes = QUERY_STRING_BUDGET_BYTES,
): string[][] => {
  if (isNil(ids) || ids.length === 0) {
    return [];
  }

  const longestIdLength = ids.reduce(
    (longest, id) => Math.max(longest, encodeURIComponent(id).length),
    0,
  );

  const bytesPerId = paramName.length + longestIdLength + 2;
  const batchSize = Math.max(1, Math.floor(budgetBytes / bytesPerId));

  return Array.from({ length: Math.ceil(ids.length / batchSize) }, (_, index) =>
    ids.slice(index * batchSize, (index + 1) * batchSize),
  );
};

export const fetchInBatches = async <TItem>({
  concurrency = DEFAULT_BATCH_CONCURRENCY,
  fetchBatch,
  ids,
  paramName,
}: {
  concurrency?: number;
  fetchBatch: (batch: string[]) => Promise<BatchQueryResult>;
  ids: string[];
  paramName: string;
}): Promise<{ data: TItem[] } | { error: FetchBaseQueryError }> => {
  const batches = chunkIdsForQuery(ids, paramName);
  const items: TItem[] = [];

  for (let index = 0; index < batches.length; index += concurrency) {
    const responses = await Promise.all(
      batches
        .slice(index, index + concurrency)
        .map((batch) => fetchBatch(batch)),
    );

    const failed = responses.find((response) => response.error);
    if (failed?.error) {
      return { error: failed.error };
    }

    responses.forEach(({ data }) => {
      if (Array.isArray(data)) {
        items.push(...(data as TItem[]));
      }
    });
  }

  return { data: items };
};

/**
 * Joins query parameters into a query string.
 * Used in the API services to join filter parameters.
 * @param params - An object containing the query parameters.
 * @returns The query string with joined parameters.
 *
 * @example
 * ```ts
 * const params = {
 *   key1: 'value1',
 *   key2: 'value2',
 *   key3: ['value3', 'value4'],
 * };
 *
 * const queryString = joinQueryParams(params);
 * "key1::value1&key2::value2&key3::value3&key3::value4"
 * ```
 */
export const joinQueryParams = (
  params: Record<string, string | number | boolean | string[]>,
) => {
  if (isNil(params)) {
    return undefined;
  }

  const queryString = Object.entries(params)
    .flatMap(([key, values]) => {
      if (Array.isArray(values)) {
        return values.map((value) => `${key}::${value}`);
      }

      return `${key}::${values}`;
    })
    .join('&');

  return queryString;
};
